import { useState } from 'react';
import { localInputToMs, msToLocalInput } from '../../lib/datetime';
import {
  cosmeticDefaults,
  missionDefaults,
  triggerDefaults,
  triggerEncounterDefaults,
  triggerEventDefaults,
  triggerGeofenceDefaults,
  triggerHealthDefaults,
  triggerItemsDefaults,
  triggerRaidDefaults,
  triggerRulesDefaults,
  withDefaults,
} from '../../lib/teamsDefaults';
import {
  COSMETIC_KINDS,
  ENCOUNTER_KINDS,
  MAX_MISSION_REWARD_TILES,
  MISSION_REWARD_KINDS,
  MISSION_REWARD_TAGS,
  MISSION_VISIBILITIES,
  TRIGGER_CURRENCY_KINDS,
  TRIGGER_HEALTH_TARGETS,
  TRIGGER_ITEM_DESTINATIONS,
  TRIGGER_LIMITS,
  TRIGGER_LIMITS_MAX,
  TRIGGER_RAID_ROOMS,
  TRIGGER_SCOPES,
  TRIGGER_STATUSES,
  WORLD_EVENT_TYPES,
  type CosmeticDef,
  type FieldErrors,
  type MissionDef,
  type MissionRewardDef,
  type TriggerDef,
  type TriggerEncounterBlock,
  type TriggerRaidBlock,
  type TriggerRulesDef,
  issuesUnder,
} from '../../lib/validate';
import {
  FORM_TRIGGER_ACTIONS,
  type FormTriggerDef,
  withEncounterKind,
  withTriggerAction,
} from './defaults';
import { triggerWindDefaults } from '../../lib/wind/defaults';
import { MissionStagesEditor } from './MissionStagesEditor';
import { IssueStoryEditor } from './s4/IssueStoryEditor';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ItemPicker,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  StackListEditor,
  TextField,
  inputCls,
} from './fields';
import type { MissionStageDef } from '../../lib/validate/scenarios';
import { useContentRefs } from './refs';
import type { FormProps } from './types';

const opts = (list: readonly string[]) => list.map((v) => ({ value: v, label: v }));

const actionHints: Record<string, string> = {
  start_encounter:
    'Starts a fight: a boss, a raid at a virtual building, or an ambush. Issues a signed raid ticket.',
  grant_items: 'Item stacks to the run’s home stock or the player’s pack.',
  grant_health:
    'Old, kept one content version (grant_wind replaces it): heals the living (clamped to max health, never revives).',
  grant_wind: 'WIND: removes rest debt (winds) from the living; never revives.',
  grant_currency: 'Adds to the wallet’s energy reserve (an energy_ledger row).',
  grant_cosmetic: 'An account-level cosmetic from the catalogue (survives death).',
  start_mission: 'Unlocks a (usually hidden) mission for the player.',
  start_event: 'Spawns a shared world event on the nearest raidable building to the player.',
};

const statusHints: Record<string, string> = {
  live: 'Codes work (within the rules).',
  coming_soon: 'Listed as a teaser; previews say coming_soon.',
  retired: 'Its codes say retired.',
};

const limitHints: Record<string, string> = {
  once: 'Once per player per scope.',
  repeatable:
    'Again after the cooldown, up to max per player (boss triggers: cooldown 0, the boss has its own).',
  unlimited: 'Only rate-limited: dev and test only (a warning on publish).',
};

/** A `datetime-local` input bound to a Unix-ms value (null = no limit). */
function DateTimeMsField({
  label,
  value,
  onChange,
  issues,
  hint,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (ms: number | null) => void;
  issues?: FieldErrors[string];
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(id, invalid) => (
        <input
          id={id}
          aria-invalid={invalid}
          type="datetime-local"
          className={inputCls}
          value={msToLocalInput(value)}
          onChange={(e) => onChange(localInputToMs(e.target.value))}
        />
      )}
    </Field>
  );
}

/** A trigger (shared-cs `TriggerDef`, ADR 0029 §2–3): basics, the action and its one block, and the rules. */
export function TriggerForm({ value, onChange, errors }: FormProps<TriggerDef>) {
  const { missions } = useContentRefs();
  const t = withDefaults(triggerDefaults(), value);
  const set = (patch: Partial<TriggerDef>) => onChange({ ...value, ...patch });
  const text = (
    k: 'id' | 'title' | 'description' | 'art' | 'codePrefix' | 'rewardsSummary',
    label: string,
    o: { hint?: string; mono?: boolean; multiline?: boolean } = {},
  ) => (
    <TextField
      label={label}
      hint={o.hint}
      mono={o.mono}
      multiline={o.multiline}
      value={t[k]}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v })}
    />
  );
  const action = t.action ?? '';

  return (
    <>
      <Fieldset legend="Trigger">
        {text('id', 'Id', { mono: true, hint: 'Boss triggers are boss_<bossId>.' })}
        {text('title', 'Title')}
        {text('description', 'Description', { multiline: true })}
        {text('art', 'Art key', { mono: true })}
        {text('codePrefix', 'Code prefix', {
          mono: true,
          hint: '2-12 capital letters, the default prefix of new codes. Cosmetic only.',
        })}
        <SelectField
          label="Status"
          value={t.status}
          options={opts(TRIGGER_STATUSES)}
          hint={statusHints[t.status ?? '']}
          issues={errors.status}
          onChange={(v) => set({ status: v ?? '' })}
        />
        <SelectField
          label="Mission"
          value={t.missionId}
          none="(none)"
          options={missions.map((m) => ({
            value: m.id ?? '',
            label: `${m.title || m.id} (${m.id})`,
          }))}
          hint="The mission this trigger belongs to (completed on a counted win)."
          issues={errors.missionId}
          onChange={(v) => set({ missionId: v })}
        />
        {text('rewardsSummary', 'Rewards summary', { hint: 'Display text.' })}
        <NumberField
          label="Payload version"
          integer
          min={1}
          value={t.payloadVersion}
          hint="Bumped when a block's meaning changes; clients list what they run."
          issues={errors.payloadVersion}
          onChange={(v) => set({ payloadVersion: v ?? 1 })}
        />
        <CheckboxField
          label="Dev only"
          hint="A tester trigger: retired unless the server runs Dev:TesterTools."
          checked={!!t.devOnly}
          onChange={(on) => set({ devOnly: on })}
        />
      </Fieldset>

      <Fieldset legend="Action">
        <SelectField
          label="Action"
          value={action}
          options={opts(FORM_TRIGGER_ACTIONS)}
          hint={`${actionHints[action] ?? ''} Switching replaces the block.`}
          issues={errors.action}
          onChange={(v) => onChange(withTriggerAction(value, v ?? ''))}
        />
        <div />
        <ActionBlock t={t} errors={errors} set={set} />
      </Fieldset>

      <RulesFieldset rules={t.rules} errors={errors} onChange={(rules) => set({ rules })} />
    </>
  );
}

/** The block for the trigger's action, and issues about blocks that don't belong (from a JSON edit). */
function ActionBlock({
  t,
  errors,
  set,
}: {
  t: TriggerDef;
  errors: FieldErrors;
  set: (patch: Partial<TriggerDef>) => void;
}) {
  const { cosmetics, missions, worldEventTypes } = useContentRefs();
  const L = TRIGGER_LIMITS_MAX;
  const stray = (
    ['encounter', 'items', 'health', 'wind', 'currency', 'cosmetic', 'mission', 'event'] as const
  ).flatMap((k) => errors[k] ?? []);
  const blockIssues = (
    <div className="sm:col-span-2">
      <IssueList issues={stray} />
    </div>
  );

  switch (t.action) {
    case 'start_encounter':
      return (
        <>
          <EncounterFields
            value={withDefaults(triggerEncounterDefaults(), t.encounter ?? {})}
            errors={errors}
            onChange={(encounter) => set({ encounter })}
          />
          {blockIssues}
        </>
      );
    case 'grant_items': {
      const g = withDefaults(triggerItemsDefaults(), t.items ?? {});
      return (
        <>
          <div className="sm:col-span-2">
            <StackListEditor
              label="Items"
              hint={`Quantities 1..${L.quantity}.`}
              base="items.stacks"
              value={(g.stacks ?? []).filter((s) => s != null)}
              errors={errors}
              onChange={(stacks) => set({ items: { ...g, stacks } })}
            />
          </div>
          <SelectField
            label="Destination"
            value={g.destination}
            options={opts(TRIGGER_ITEM_DESTINATIONS)}
            hint="home = the run's arrived-home stock (safe from raid death); carried = the player's own team pack."
            issues={errors['items.destination']}
            onChange={(v) => set({ items: { ...g, destination: v ?? '' } })}
          />
          {blockIssues}
        </>
      );
    }
    case 'grant_wind': {
      const w = { ...triggerWindDefaults(), ...((t as FormTriggerDef).wind ?? {}) };
      return (
        <>
          <NumberField
            label="Winds"
            min={0.5}
            max={3}
            step={0.25}
            value={w.winds}
            hint="0.5..3 winds of rest debt removed."
            issues={errors['wind.winds']}
            onChange={(v) => set({ wind: { ...w, winds: v ?? 0 } } as Partial<TriggerDef>)}
          />
          <SelectField
            label="Target"
            value={w.target}
            options={opts(TRIGGER_HEALTH_TARGETS)}
            hint="team = the team riding with the player."
            issues={errors['wind.target']}
            onChange={(v) => set({ wind: { ...w, target: v ?? '' } } as Partial<TriggerDef>)}
          />
          {blockIssues}
        </>
      );
    }
    case 'grant_health': {
      const h = withDefaults(triggerHealthDefaults(), t.health ?? {});
      return (
        <>
          <NumberField
            label="Health"
            integer
            min={1}
            max={L.health}
            value={h.amount}
            hint={`1..${L.health}, clamped to max health.`}
            issues={errors['health.amount']}
            onChange={(v) => set({ health: { ...h, amount: v ?? 0 } })}
          />
          <SelectField
            label="Target"
            value={h.target}
            options={opts(TRIGGER_HEALTH_TARGETS)}
            hint="team = the team riding with the player."
            issues={errors['health.target']}
            onChange={(v) => set({ health: { ...h, target: v ?? '' } })}
          />
          {blockIssues}
        </>
      );
    }
    case 'grant_currency': {
      const c = { kind: 'energy_reserve', amount: 0, ...(t.currency ?? {}) };
      return (
        <>
          <SelectField
            label="Currency"
            value={c.kind}
            options={opts(TRIGGER_CURRENCY_KINDS)}
            issues={errors['currency.kind']}
            onChange={(v) => set({ currency: { ...c, kind: v ?? '' } })}
          />
          <NumberField
            label="Amount"
            integer
            min={1}
            max={L.energy}
            value={c.amount}
            hint={`1..${L.energy} energy. Not a purchase: doesn't mark the run assisted.`}
            issues={errors['currency.amount']}
            onChange={(v) => set({ currency: { ...c, amount: v ?? 0 } })}
          />
          {blockIssues}
        </>
      );
    }
    case 'grant_cosmetic':
      return (
        <>
          <SelectField
            label="Cosmetic"
            value={t.cosmetic?.cosmeticId}
            options={cosmetics.map((c) => ({
              value: c.id ?? '',
              label: `${c.name || c.id} (${c.id}) · ${c.kind ?? ''}`,
            }))}
            issues={errors['cosmetic.cosmeticId']}
            onChange={(v) => set({ cosmetic: { cosmeticId: v ?? '' } })}
          />
          {blockIssues}
        </>
      );
    case 'start_mission':
      return (
        <>
          <SelectField
            label="Mission to unlock"
            value={t.mission?.missionId}
            options={missions.map((m) => ({
              value: m.id ?? '',
              label: `${m.title || m.id} (${m.id}) · ${m.visibility ?? 'listed'}`,
            }))}
            hint="Usually a hidden mission."
            issues={errors['mission.missionId']}
            onChange={(v) => set({ mission: { missionId: v ?? '' } })}
          />
          {blockIssues}
        </>
      );
    case 'start_event': {
      const e = withDefaults(triggerEventDefaults(), t.event ?? {});
      const known = new Set(worldEventTypes.map((w) => w.id));
      return (
        <>
          <SelectField
            label="Event type"
            value={e.type}
            options={WORLD_EVENT_TYPES.map((w) => ({
              value: w,
              label: known.has(w) ? w : `${w} (not in world events)`,
            }))}
            issues={errors['event.type']}
            onChange={(v) => set({ event: { ...e, type: v ?? '' } })}
          />
          <NumberField
            label="Max distance (m)"
            min={1}
            max={L.eventDistanceM}
            value={e.maxDistanceM}
            hint={`The nearest raidable building within this of the player, at most ${L.eventDistanceM} m.`}
            issues={errors['event.maxDistanceM']}
            onChange={(v) => set({ event: { ...e, maxDistanceM: v ?? 0 } })}
          />
          {blockIssues}
        </>
      );
    }
    default:
      return <p className="text-xs text-bruise sm:col-span-2">Pick an action to edit its block.</p>;
  }
}

function EncounterFields({
  value,
  errors,
  onChange,
}: {
  value: TriggerEncounterBlock;
  errors: FieldErrors;
  onChange: (next: TriggerEncounterBlock) => void;
}) {
  const { bosses, encounterTypes, categories, lootTables, zombies } = useContentRefs();
  const kind = value.kind ?? '';
  const raid = withDefaults(triggerRaidDefaults(), value.raid ?? {});
  const setRaid = (patch: Partial<TriggerRaidBlock>) =>
    onChange({ ...value, raid: { ...raid, ...patch } });
  const zombieOptions = zombies.map((z) => ({ value: z.id ?? '', label: z.id ?? '' }));
  const R = TRIGGER_RAID_ROOMS;

  return (
    <>
      <SelectField
        label="Encounter kind"
        value={kind}
        options={opts(ENCOUNTER_KINDS)}
        hint="boss: a boss fight; raid: a location-free raid at a virtual building; ambush: a route-encounter fight."
        issues={errors['encounter.kind']}
        onChange={(v) => onChange(withEncounterKind(value, v ?? ''))}
      />
      {kind === 'boss' && (
        <SelectField
          label="Boss"
          value={value.bossId}
          options={bosses.map((b) => ({ value: b.id ?? '', label: `${b.name || b.id} (${b.id})` }))}
          hint="The boss keeps its own cooldown, energy cost and first clear; use limit repeatable with cooldown 0."
          issues={errors['encounter.bossId']}
          onChange={(v) => onChange({ ...value, bossId: v ?? '' })}
        />
      )}
      {kind === 'ambush' && (
        <SelectField
          label="Encounter fight"
          value={value.encounterId}
          options={encounterTypes
            .filter((e) => (e.roomCount ?? 0) > 0)
            .map((e) => ({ value: e.id ?? '', label: e.id ?? '' }))}
          hint="An encounters type with rooms (roomCount above 0)."
          issues={errors['encounter.encounterId']}
          onChange={(v) => onChange({ ...value, encounterId: v ?? '' })}
        />
      )}
      {kind === 'raid' && (
        <>
          <SelectField
            label="Category"
            value={raid.category}
            options={categories.map((c) => ({ value: c.id ?? '', label: c.id ?? '' }))}
            hint="Picks the room templates."
            issues={errors['encounter.raid.category']}
            onChange={(v) => setRaid({ category: v ?? '' })}
          />
          <NumberField
            label="Difficulty"
            integer
            min={1}
            max={5}
            value={raid.difficulty}
            issues={errors['encounter.raid.difficulty']}
            onChange={(v) => setRaid({ difficulty: v ?? 1 })}
          />
          <SelectField
            label="Loot table"
            value={raid.lootTableId}
            options={lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }))}
            issues={errors['encounter.raid.lootTableId']}
            onChange={(v) => setRaid({ lootTableId: v ?? '' })}
          />
          <NumberField
            label="Loot rolls"
            integer
            min={0}
            value={raid.lootRolls}
            issues={errors['encounter.raid.lootRolls']}
            onChange={(v) => setRaid({ lootRolls: v ?? 0 })}
          />
          <NumberField
            label="Rooms"
            integer
            min={R.min}
            max={R.max}
            value={raid.rooms}
            hint={`${R.min}..${R.max}.`}
            issues={errors['encounter.raid.rooms']}
            onChange={(v) => setRaid({ rooms: v ?? 0 })}
          />
          <ListEditor
            label="Zombies"
            hint="Picked evenly (a repeated id weighs more). Empty = the raid rules' spawn weights."
            items={raid.zombieIds ?? []}
            what="zombie"
            issues={errors['encounter.raid.zombieIds']}
            make={() => zombies[0]?.id ?? ''}
            onChange={(zombieIds) => setRaid({ zombieIds })}
            row={(z, j, setRow) => (
              <>
                <SelectInput
                  label={`Raid zombie ${j + 1}`}
                  value={z}
                  options={zombieOptions}
                  invalid={!!errors[`encounter.raid.zombieIds.${j}`]?.length}
                  onChange={(v) => setRow(v ?? '')}
                />
                <IssueList issues={errors[`encounter.raid.zombieIds.${j}`]} />
              </>
            )}
          />
        </>
      )}
      {kind !== 'boss' && kind !== 'ambush' && kind !== 'raid' && (
        <p className="text-xs text-bruise">Pick a kind.</p>
      )}
      <div className="sm:col-span-2">
        <IssueList
          issues={[
            ...(errors['encounter.raid'] ?? []),
            ...(errors['encounter.bossId'] && kind !== 'boss' ? errors['encounter.bossId'] : []),
            ...(errors['encounter.encounterId'] && kind !== 'ambush'
              ? errors['encounter.encounterId']
              : []),
          ]}
        />
      </div>
    </>
  );
}

/** Trigger rules (ADR 0029 §3). Absent fields show the C# defaults; the window is edited as local time, stored as Unix ms. */
function RulesFieldset({
  rules,
  errors,
  onChange,
}: {
  rules: TriggerRulesDef | null | undefined;
  errors: FieldErrors;
  onChange: (next: TriggerRulesDef) => void;
}) {
  if (rules === null)
    return (
      <Fieldset legend="Rules">
        <div className="sm:col-span-2">
          <IssueList issues={errors.rules} />
          <button
            type="button"
            className="rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold"
            onClick={() => onChange(triggerRulesDefaults())}
          >
            Add default rules
          </button>
        </div>
      </Fieldset>
    );
  const r = withDefaults(triggerRulesDefaults(), rules ?? {});
  const set = (patch: Partial<TriggerRulesDef>) => onChange({ ...r, ...patch });
  const g = r.geofence ? withDefaults(triggerGeofenceDefaults(), r.geofence) : null;
  const setGeo = (patch: Partial<NonNullable<typeof g>>) => set({ geofence: { ...g, ...patch } });

  return (
    <Fieldset legend="Rules">
      <SelectField
        label="Scope"
        value={r.scope}
        options={opts(TRIGGER_SCOPES)}
        hint="run: a new run can redeem again. account: once per account."
        issues={errors['rules.scope']}
        onChange={(v) => set({ scope: v ?? '' })}
      />
      <SelectField
        label="Limit"
        value={r.limit}
        options={opts(TRIGGER_LIMITS)}
        hint={limitHints[r.limit ?? '']}
        issues={errors['rules.limit']}
        onChange={(v) => set({ limit: v ?? '' })}
      />
      <NumberField
        label="Cooldown (h)"
        min={0}
        step={0.5}
        value={r.cooldownHours}
        hint="repeatable only (0 = none)."
        issues={errors['rules.cooldownHours']}
        onChange={(v) => set({ cooldownHours: v ?? 0 })}
      />
      <NumberField
        label="Max per player"
        integer
        nullable
        min={1}
        value={r.maxPerPlayer}
        hint="repeatable only: redeems per player per scope. Empty = no cap."
        issues={errors['rules.maxPerPlayer']}
        onChange={(v) => set({ maxPerPlayer: v })}
      />
      <NumberField
        label="Max total"
        integer
        nullable
        min={1}
        value={r.maxTotal}
        hint="A global cap across all players (first N finders). Empty = none."
        issues={errors['rules.maxTotal']}
        onChange={(v) => set({ maxTotal: v })}
      />
      <TextField
        label="Season"
        mono
        value={r.season ?? ''}
        hint="Only redeemable in this season (the server's Triggers:Season). Empty = any."
        issues={errors['rules.season']}
        onChange={(v) => set({ season: v === '' ? null : v })}
      />
      <DateTimeMsField
        label="Active from"
        value={r.activeFrom}
        hint="Local time; empty = no start."
        issues={errors['rules.activeFrom']}
        onChange={(activeFrom) => set({ activeFrom })}
      />
      <DateTimeMsField
        label="Active until"
        value={r.activeUntil}
        hint="Local time; empty = no end."
        issues={errors['rules.activeUntil']}
        onChange={(activeUntil) => set({ activeUntil })}
      />
      <CheckboxField
        label="Requires a live run"
        checked={r.requiresLiveRun !== false}
        onChange={(on) => set({ requiresLiveRun: on })}
      />
      <CheckboxField
        label="Requires the app"
        hint="The web can show it but not redeem."
        checked={r.requiresApp !== false}
        onChange={(on) => set({ requiresApp: on })}
      />
      <div className="sm:col-span-2">
        <CheckboxField
          label="Geofence"
          hint="The redeem must happen inside this circle, with a location fix."
          checked={g !== null}
          onChange={(on) => set({ geofence: on ? triggerGeofenceDefaults() : null })}
        />
        <IssueList issues={errors['rules.geofence']} />
      </div>
      {g && (
        <>
          <NumberField
            label="Latitude"
            min={-90}
            max={90}
            value={g.lat}
            onChange={(v) => setGeo({ lat: v ?? 0 })}
          />
          <NumberField
            label="Longitude"
            min={-180}
            max={180}
            value={g.lon}
            onChange={(v) => setGeo({ lon: v ?? 0 })}
          />
          <NumberField
            label="Radius (m)"
            min={1}
            value={g.radiusM}
            issues={errors['rules.geofence.radiusM']}
            onChange={(v) => setGeo({ radiusM: v ?? 0 })}
          />
        </>
      )}
    </Fieldset>
  );
}

/** A mission (shared-cs `MissionDef`, ADR 0029 §10). */
export function MissionForm({ value, onChange, errors }: FormProps<MissionDef>) {
  const { triggers } = useContentRefs();
  const [tab, setTab] = useState<'mission' | 'story'>(value.story ? 'story' : 'mission');
  const m = withDefaults(missionDefaults(), value);
  const set = (patch: Partial<MissionDef>) => onChange({ ...value, ...patch });
  const text = (
    k: 'id' | 'title' | 'kicker' | 'briefing' | 'art' | 'rewardsSummary',
    label: string,
    o: { hint?: string; mono?: boolean; multiline?: boolean } = {},
  ) => (
    <TextField
      label={label}
      hint={o.hint}
      mono={o.mono}
      multiline={o.multiline}
      value={m[k]}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v })}
    />
  );
  const storyIssues = issuesUnder(errors, 'story');
  const tabs = (
    <div role="tablist" aria-label="Mission editor" className="mb-3 flex gap-1">
      {(['mission', 'story'] as const).map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={tab === t}
          onClick={() => setTab(t)}
          className={`rounded border-2 border-ink px-3 py-0.5 text-sm font-semibold ${tab === t ? 'bg-benday/30' : 'bg-white hover:bg-caption/30'}`}
        >
          {t === 'mission' ? 'Mission' : value.story ? 'Story' : 'Story (none)'}
          {t === 'story' && storyIssues.length > 0 && (
            <span className="ml-1 text-blam">({storyIssues.length})</span>
          )}
        </button>
      ))}
    </div>
  );
  if (tab === 'story')
    return (
      <>
        {tabs}
        <IssueStoryEditor mission={value} errors={errors} onChange={(story) => set({ story })} />
      </>
    );
  return (
    <>
      {tabs}
      <Fieldset legend="Mission">
        {text('id', 'Id', { mono: true, hint: 'Boss missions are mission_<bossId>.' })}
        {text('title', 'Title')}
        {text('kicker', 'Kicker', { hint: 'The small line above the title, e.g. WANTED.' })}
        {text('art', 'Art key', { mono: true })}
        {text('briefing', 'Briefing', { multiline: true })}
        {text('rewardsSummary', 'Rewards summary', { multiline: true })}
        <SelectField
          label="Trigger"
          value={m.triggerId}
          options={triggers.map((t) => ({
            value: t.id ?? '',
            label: `${t.title || t.id} (${t.id})`,
          }))}
          hint="The trigger its QR fires (optional for a staged mission)."
          issues={errors.triggerId}
          onChange={(v) => set({ triggerId: v ?? '' })}
        />
        <NumberField
          label="Order"
          integer
          min={undefined}
          value={m.order}
          hint="Sort order on the Missions page; unique."
          issues={errors.order}
          onChange={(v) => set({ order: v ?? 0 })}
        />
        <SelectField
          label="Visibility"
          value={m.visibility}
          options={opts(MISSION_VISIBILITIES)}
          hint="hidden: shown once a start_mission trigger unlocks it."
          issues={errors.visibility}
          onChange={(v) => set({ visibility: v ?? '' })}
        />
        <SelectField
          label="Status"
          value={m.status}
          options={opts(TRIGGER_STATUSES)}
          issues={errors.status}
          onChange={(v) => set({ status: v ?? '' })}
        />
        <SelectField
          label="Scope"
          value={m.scope}
          options={opts(TRIGGER_SCOPES)}
          hint="Whether completion resets with a new run."
          issues={errors.scope}
          onChange={(v) => set({ scope: v ?? '' })}
        />
        <CheckboxField
          label="Dev only"
          hint="Listed and unlockable only with Dev:TesterTools."
          checked={!!m.devOnly}
          onChange={(on) => set({ devOnly: on })}
        />
      </Fieldset>
      <MissionRewardsEditor
        value={(value.rewards ?? []).filter((r): r is MissionRewardDef => r != null)}
        errors={errors}
        onChange={(rewards) => set({ rewards })}
      />
      <MissionStagesEditor
        value={(value.stages ?? []) as MissionStageDef[]}
        errors={errors}
        onChange={(stages) => set({ stages: stages as MissionDef['stages'] })}
      />
    </>
  );
}

/**
 * A mission's reward tiles (`missions[].rewards`, shared-cs `MissionRewardDef`, ADR 0062): what the MISSIONS screen draws. Display data:
 * the grants still come from the boss, scenario or story rules. Story issues leave this empty (their tiles come from story.rewards).
 */
export function MissionRewardsEditor({
  value,
  onChange,
  errors,
}: {
  value: MissionRewardDef[];
  onChange: (next: MissionRewardDef[]) => void;
  errors: FieldErrors;
}) {
  const { cosmetics } = useContentRefs();
  const at = (j: number, k: string) => errors[`rewards.${j}.${k}`];
  return (
    <Fieldset legend="Reward tiles (MISSIONS)">
      <ListEditor
        label="Tiles"
        what="tile"
        hint={`At most ${MAX_MISSION_REWARD_TILES}. Quantity 0 shows "?" (a haul). The name defaults to the item's or cosmetic's.`}
        items={value}
        issues={errors.rewards}
        onChange={onChange}
        make={() => ({ kind: 'item', refId: '', name: '', quantity: 1, tag: 'first_clear' })}
        row={(r, j, put) => (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <SelectField
              label="Kind"
              value={r.kind ?? 'item'}
              options={opts(MISSION_REWARD_KINDS)}
              issues={at(j, 'kind')}
              onChange={(v) => put({ ...r, kind: v ?? 'item', refId: '' })}
            />
            {r.kind === 'cosmetic' ? (
              <SelectField
                label="Cosmetic"
                value={r.refId ?? ''}
                options={cosmetics.map((c) => ({
                  value: c.id ?? '',
                  label: `${c.name || c.id} (${c.id})`,
                }))}
                issues={at(j, 'refId')}
                onChange={(v) => put({ ...r, refId: v ?? '' })}
              />
            ) : r.kind === 'item' || r.kind === 'haul' || r.kind == null ? (
              <Field label="Item" issues={at(j, 'refId')}>
                {(id, invalid) => (
                  <ItemPicker
                    id={id}
                    invalid={invalid}
                    label={`Tile ${j + 1} item`}
                    none={r.kind === 'haul' ? '(none)' : undefined}
                    value={r.refId ?? ''}
                    onChange={(v) => put({ ...r, refId: v ?? '' })}
                  />
                )}
              </Field>
            ) : (
              <div />
            )}
            <TextField
              label="Name"
              value={r.name ?? ''}
              issues={at(j, 'name')}
              onChange={(v) => put({ ...r, name: v })}
            />
            <NumberField
              label="Quantity"
              integer
              min={0}
              max={999}
              value={r.quantity ?? 1}
              issues={at(j, 'quantity')}
              onChange={(v) => put({ ...r, quantity: v ?? 0 })}
            />
            <SelectField
              label="Tag"
              value={r.tag ?? 'first_clear'}
              options={opts(MISSION_REWARD_TAGS)}
              issues={at(j, 'tag')}
              onChange={(v) => put({ ...r, tag: v ?? 'first_clear' })}
            />
          </div>
        )}
      />
    </Fieldset>
  );
}

/** A cosmetic (shared-cs `CosmeticDef`). */
export function CosmeticForm({ value, onChange, errors }: FormProps<CosmeticDef>) {
  const c = withDefaults(cosmeticDefaults(), value);
  const set = (patch: Partial<CosmeticDef>) => onChange({ ...value, ...patch });
  return (
    <Fieldset legend="Cosmetic">
      <TextField
        label="Id"
        mono
        value={c.id}
        hint="Store products' cosmeticIds and grant_cosmetic triggers use it."
        issues={errors.id}
        onChange={(v) => set({ id: v })}
      />
      <TextField
        label="Name"
        value={c.name}
        issues={errors.name}
        onChange={(v) => set({ name: v })}
      />
      <SelectField
        label="Kind"
        value={c.kind}
        options={opts(COSMETIC_KINDS)}
        issues={errors.kind}
        onChange={(v) => set({ kind: v ?? '' })}
      />
    </Fieldset>
  );
}
